import { position, autoUpdate } from '../floating.js';

const SHOW_DELAY = 400;
const HIDE_DELAY = 100;
// Once a tooltip was visible, neighbours show instantly (like native toolbars).
const WARM_WINDOW = 600;
let warmUntil = 0;
let openTooltip = null;

/**
 * <mellow-tooltip>: shows its [role="tooltip"] on hover and keyboard focus.
 * Follows WCAG 1.4.13: dismissible with Escape, hoverable, persistent while hovered.
 */
export class MellowTooltip extends HTMLElement {
    connectedCallback() {
        if (this._ready) return this.sync();
        this._ready = true;

        this.addEventListener('pointerenter', this.onEnter, true);
        this.addEventListener('pointerleave', this.onLeave, true);
        this.addEventListener('focusin', this.onFocus);
        this.addEventListener('focusout', () => this.hide());
        this.addEventListener('click', this.onClick);

        this.sync();
    }

    disconnectedCallback() {
        this.hide(true);
    }

    get tip() {
        return this.querySelector(':scope > [role="tooltip"]');
    }

    get trigger() {
        return [...this.children].find((child) => child !== this.tip);
    }

    get target() {
        const trigger = this.trigger;
        return trigger?.matches('button, a, input, [tabindex]') ? trigger : trigger?.querySelector('button, a, input, [tabindex]') ?? trigger;
    }

    /** Describe the trigger – or label it, when it has no accessible name of its own (icon buttons). */
    sync() {
        const { target, tip } = this;
        if (!target || !tip) return;

        const named = target.hasAttribute('aria-label') || target.hasAttribute('aria-labelledby') || target.textContent.trim() !== '';
        // Don't repeat what the label already says (icon buttons labelled with the tooltip text).
        if (named && target.getAttribute('aria-label')?.trim() === tip.textContent.trim()) return;
        if (named) {
            if (target.getAttribute('aria-labelledby') !== tip.id) target.setAttribute('aria-describedby', tip.id);
        } else {
            target.setAttribute('aria-labelledby', tip.id);
        }
    }

    onEnter = (event) => {
        if (event.pointerType === 'touch' || this.hasAttribute('toggleable')) return;
        clearTimeout(this._timer);
        const delay = Date.now() < warmUntil || this.tip?.matches(':popover-open') ? 0 : SHOW_DELAY;
        this._timer = setTimeout(() => this.show(), delay);
    };

    onLeave = (event) => {
        if (this.hasAttribute('toggleable')) return;
        // Moving between trigger and tooltip keeps it open.
        if (this.contains(event.relatedTarget)) return;
        clearTimeout(this._timer);
        this._timer = setTimeout(() => this.hide(), HIDE_DELAY);
    };

    onFocus = (event) => {
        if (!event.target.matches(':focus-visible')) return;
        // Focus can arrive while another popover or dialog is opening (a modal focusing its
        // first control): showing a popover then throws. Show on the next frame, if still focused.
        cancelAnimationFrame(this._focusFrame);
        this._focusFrame = requestAnimationFrame(() => {
            if (this.contains(document.activeElement)) this.show();
        });
    };

    onClick = (event) => {
        if (!this.hasAttribute('toggleable') || this.tip?.contains(event.target)) return;
        this.tip?.matches(':popover-open') ? this.hide() : this.show();
    };

    onKeydown = (event) => {
        if (event.key === 'Escape') this.hide();
    };

    show() {
        const tip = this.tip;
        const anchor = this.target;
        if (!tip || !anchor || tip.matches(':popover-open') || anchor.matches(':disabled')) return;

        if (openTooltip && openTooltip !== this) openTooltip.hide(true);
        openTooltip = this;

        tip.showPopover();
        const placement = this.getAttribute('position') ?? 'top';
        const align = this.getAttribute('align') ?? 'center';
        this._cleanup = autoUpdate(anchor, tip, () => position(anchor, tip, { placement, align, offset: 6 }));
        document.addEventListener('keydown', this.onKeydown);
    }

    hide(immediate = false) {
        clearTimeout(this._timer);
        cancelAnimationFrame(this._focusFrame);
        const tip = this.tip;
        if (!tip?.matches(':popover-open')) return;

        tip.hidePopover();
        this._cleanup?.();
        document.removeEventListener('keydown', this.onKeydown);
        if (openTooltip === this) openTooltip = null;
        if (!immediate) warmUntil = Date.now() + WARM_WINDOW;
    }
}
